import {
  PRODUCT_BY_ID,
  PRODUCT_IDS,
  PRODUCTS,
  normalizeProductId,
  type ProductId,
} from '@/config/products';
import { getHomeRouteForRole } from '@/utils/homeRoute';
import { resolvePostLoginRoute } from '@/utils/workspaceRouting';
import type { WorkspaceContextPayload } from '@/config/productRegistry';

export interface TenantProductAccess {
  enabled: boolean;
  status?: 'active' | 'coming_soon' | 'trial' | 'expired';
  trialEndsAt?: string | null;
  daysLeft?: number | null;
  needsPayment?: boolean;
}

export type TenantProductsMap = Record<ProductId, TenantProductAccess>;

const ADMIN_ROLES = new Set(['SUPER_ADMIN', 'ADMIN']);

export function isAdminRole(role?: string | null): boolean {
  return !!role && ADMIN_ROLES.has(role);
}

/** Prod-safe defaults — every existing org gets PM only. */
const DEFAULTS: TenantProductsMap = Object.fromEntries(
  PRODUCTS.map((product) => [
    product.id,
    {
      enabled: product.id === 'pm',
      status: product.catalogStatus === 'available' ? 'active' : 'coming_soon',
    },
  ])
) as TenantProductsMap;

function parseProductEntry(
  productId: ProductId,
  raw: unknown,
  fallback: TenantProductAccess
): TenantProductAccess {
  if (!raw || typeof raw !== 'object') {
    return fallback;
  }

  const entry = raw as Record<string, unknown>;

  const trialEndsAt =
    typeof entry.trialEndsAt === 'string' ? entry.trialEndsAt : undefined;

  if (productId === 'pm') {
    return {
      enabled: entry.enabled !== false,
      status:
        typeof entry.status === 'string'
          ? (entry.status as TenantProductAccess['status'])
          : fallback.status,
      trialEndsAt,
    };
  }

  return {
    enabled: entry.enabled === true,
    status:
      typeof entry.status === 'string'
        ? (entry.status as TenantProductAccess['status'])
        : fallback.status,
    trialEndsAt,
  };
}

/** Read org product entitlements from tenant.settings (backward compatible; watch → lenc). */
export function parseTenantProducts(
  settings?: Record<string, unknown> | null
): TenantProductsMap {
  const rawProducts = settings?.products;
  if (!rawProducts || typeof rawProducts !== 'object') {
    return { ...DEFAULTS };
  }

  const products = rawProducts as Record<string, unknown>;
  const lencRaw = products.lenc ?? products.watch;

  return Object.fromEntries(
    PRODUCT_IDS.map((productId) => {
      const raw = productId === 'lenc' ? lencRaw : products[productId];
      return [
        productId,
        parseProductEntry(productId, raw, DEFAULTS[productId]),
      ];
    })
  ) as TenantProductsMap;
}

export function getEnabledProductIds(access: TenantProductsMap): ProductId[] {
  return PRODUCT_IDS.filter((id) => access[id]?.enabled === true);
}

/** Products shown in launcher — admins see full catalog. */
export function getVisibleProductIds(
  access: TenantProductsMap,
  role?: string | null
): ProductId[] {
  if (isAdminRole(role)) {
    return [...PRODUCT_IDS];
  }
  return PRODUCT_IDS.filter((id) => {
    const entry = access[id];
    if (!entry?.enabled) return false;
    return PRODUCT_BY_ID[id].catalogStatus === 'available' || entry.enabled;
  });
}

export function getVisibleProducts(
  access: TenantProductsMap,
  role?: string | null
) {
  const ids = getVisibleProductIds(access, role);
  return PRODUCTS.filter((p) => ids.includes(p.id));
}

export function isProductEntitled(productId: ProductId, access: TenantProductsMap): boolean {
  const entry = access[productId];
  if (!entry?.enabled) return false;
  if (PRODUCT_BY_ID[productId].catalogStatus === 'coming_soon') return false;
  return true;
}

export type ProductLaunchAction = 'launch' | 'preview' | 'denied';

export function canLaunchProduct(
  productId: ProductId,
  access: TenantProductsMap,
  role?: string | null
): ProductLaunchAction {
  if (isProductEntitled(productId, access)) {
    return 'launch';
  }
  if (isAdminRole(role)) {
    return 'preview';
  }
  return 'denied';
}

/** Catalog or entitlement — product is visible but not openable yet. */
export function isProductComingSoon(
  productId: ProductId,
  access: TenantProductsMap
): boolean {
  if (isProductEntitled(productId, access)) return false;
  return true;
}

/** Open product app/routes (Plan board, etc.) — not the coming-soon peek. */
export function canOpenProduct(
  productId: ProductId,
  access: TenantProductsMap,
  role?: string | null
): boolean {
  return canLaunchProduct(productId, access, role) === 'launch';
}

/** Hub when admin sees 2+ products OR member has 2+ entitled available products. */
export function shouldShowAidlcHub(
  access: TenantProductsMap,
  role?: string | null
): boolean {
  if (isAdminRole(role)) {
    return PRODUCT_IDS.length >= 2;
  }
  const entitled = getEnabledProductIds(access).filter(
    (id) => PRODUCT_BY_ID[id].catalogStatus === 'available'
  );
  return entitled.length > 1;
}

/** @deprecated Use shouldShowAidlcHub */
export function shouldShowWorkspaceHub(
  access: TenantProductsMap,
  role?: string | null
): boolean {
  return shouldShowAidlcHub(access, role);
}

export function getActiveProductId(pathname: string, search = ''): ProductId {
  if (pathname.startsWith('/workspace')) {
    const preview = new URLSearchParams(search).get('product');
    const normalized = preview ? normalizeProductId(preview) : null;
    if (normalized && PRODUCT_BY_ID[normalized]) return normalized;
  }

  if (pathname.startsWith('/pm')) return 'pm';

  const routed = [...PRODUCTS]
    .filter((product) => product.routePrefix !== '/')
    .sort((a, b) => b.routePrefix.length - a.routePrefix.length)
    .find((product) => pathname.startsWith(product.routePrefix));

  return routed?.id ?? 'pm';
}

export function getProductEntryRoute(productId: ProductId, role?: string | null): string {
  if (productId === 'pm') return getHomeRouteForRole(role);
  const product = PRODUCT_BY_ID[productId];
  if (product.launchMode === 'internal') {
    return product.routePrefix;
  }
  return `/workspace?product=${productId}`;
}

/** Build external launch URL with optional SSO token query param. */
export function getProductLaunchUrl(
  productId: ProductId,
  options?: { exchangeToken?: string; preview?: boolean }
): string | null {
  const product = PRODUCT_BY_ID[productId];
  if (!product.appUrl) return null;
  const url = new URL(product.appUrl);
  url.pathname = '/auth/callback';
  if (options?.exchangeToken) {
    url.searchParams.set('token', options.exchangeToken);
  }
  if (options?.preview) {
    url.searchParams.set('preview', '1');
  }
  return url.toString();
}

/** Post-login landing — respects entry product, role, org defaults (via workspace context). */
export function getPostLoginRoute(
  settings: Record<string, unknown> | undefined | null,
  role?: string | null,
  entryProductId?: ProductId | null,
  workspace?: WorkspaceContextPayload | null,
): string {
  return resolvePostLoginRoute({ settings, role, entryProductId, workspace });
}
